import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { NotaryServiceCounties } from "@/components/server/NotaryServiceCounties";
import { FileSignature, ShieldCheck, CheckCircle2, Eye, Download, Loader2 } from "lucide-react";

const STATUS_META = {
  admin_verified: { label: "Verified by ARPS", tone: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  self_reported: { label: "Self-reported", tone: "border-sky-200 bg-sky-50 text-sky-700" },
  not_provided: { label: "Not provided", tone: "border-slate-200 bg-slate-50 text-slate-500" },
};
const PLATFORM_LABEL = { snapdocs: "Snapdocs", "notary rotary": "Notary Rotary", "123notary": "123Notary", other: "Other" };

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-sm text-slate-800">{value || "—"}</p>
    </div>
  );
}

export const AdminNotaryPanel = ({ serverId, signingAgent, onChanged }) => {
  const [sa, setSa] = useState(signingAgent || { verification_status: "not_provided" });
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(false);

  useEffect(() => { setSa(signingAgent || { verification_status: "not_provided" }); }, [signingAgent]);

  const verify = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/servers/${serverId}/signing-agent/verify`, { verification_status: status });
      setSa(data);
      toast.success("Signing-agent classification updated");
      if (onChanged) onChanged(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  const openFile = async (inline) => {
    if (!sa.file_id) return;
    setViewing(true);
    try {
      const { data } = await api.post(`/files/${sa.file_id}/view-token`);
      const url = `${API}/files/${sa.file_id}?token=${encodeURIComponent(data.token)}${inline ? "&inline=true" : ""}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open file");
    } finally {
      setViewing(false);
    }
  };

  const sm = STATUS_META[sa.verification_status] || STATUS_META.not_provided;
  const hasData = sa.nna_number || sa.platform || sa.profile_ref || sa.experience_years != null || sa.signings_completed != null || sa.file_id;

  return (
    <div className="space-y-4" data-testid="admin-notary-panel">
      {/* Signing-agent classification / verification */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="admin-signing-agent">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
              <FileSignature className="h-5 w-5 text-indigo-500" /> Signing Agent
            </h3>
            <p className="mt-1 text-sm text-slate-500">Loan-signing credentials the provider self-reported. Verify to boost their loan-signing priority. This never affects account approval or required documents.</p>
          </div>
          <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${sm.tone}`} data-testid="admin-signing-agent-status">
            {sa.verification_status === "admin_verified" ? <ShieldCheck className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />} {sm.label}
          </span>
        </div>

        {!hasData ? (
          <p className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-500" data-testid="admin-signing-agent-empty">This provider has not added any signing-agent details.</p>
        ) : (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="NNA number" value={sa.nna_number} />
            <Field label="Platform" value={PLATFORM_LABEL[sa.platform] || sa.platform} />
            <Field label="Profile ID / URL" value={sa.profile_ref} />
            <Field label="Experience (years)" value={sa.experience_years != null ? String(sa.experience_years) : ""} />
            <Field label="Signings completed" value={sa.signings_completed != null ? String(sa.signings_completed) : ""} />
            {sa.file_id && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Supporting file</p>
                <div className="mt-1 flex gap-2">
                  <Button size="sm" variant="outline" disabled={viewing} data-testid="admin-signing-agent-view" onClick={() => openFile(true)}>
                    {viewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Eye className="mr-1 h-3.5 w-3.5" /> View</>}
                  </Button>
                  <Button size="sm" variant="outline" disabled={viewing} data-testid="admin-signing-agent-download" onClick={() => openFile(false)}>
                    <Download className="mr-1 h-3.5 w-3.5" /> Download
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
          <span className="mr-1 self-center text-xs font-semibold text-slate-500">Set classification:</span>
          <Button size="sm" disabled={busy || sa.verification_status === "admin_verified"} data-testid="admin-verify-admin_verified" onClick={() => verify("admin_verified")} className="bg-emerald-600 text-white hover:bg-emerald-700">Verified</Button>
          <Button size="sm" variant="outline" disabled={busy || sa.verification_status === "self_reported"} data-testid="admin-verify-self_reported" onClick={() => verify("self_reported")}>Self-reported</Button>
          <Button size="sm" variant="outline" disabled={busy || sa.verification_status === "not_provided"} data-testid="admin-verify-not_provided" onClick={() => verify("not_provided")} className="border-red-300 text-red-600 hover:bg-red-50">Not provided</Button>
        </div>
      </div>

      {/* Service counties — admin view/edit (same validation + audit as provider) */}
      <NotaryServiceCounties
        getUrl={`/admin/servers/${serverId}/service-counties`}
        putUrl={`/admin/servers/${serverId}/service-counties`}
        title="Notary service counties"
        subtitle="View and edit the counties this provider covers. Edits are validated against the authoritative county list and recorded in the provider's onboarding log."
        onSaved={onChanged}
      />
    </div>
  );
};
